import React from 'react';
import { 
  Grid, 
  GridItem, 
  Box, 
  Container, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Card, 
  CardBody, 
  SimpleGrid,
  Center,
  Badge,
  List,
  ListItem,
  ListIcon,
  Icon
} from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import { 
  FiLink, 
  FiFolder, 
  FiMonitor,
  FiCheckCircle,
  FiSettings,
  FiShield,
  FiZap,
  FiUsers,
  FiPlay
} from 'react-icons/fi';

const RevedyImplementation = () => {
  const integrationOptions = [
    {
      title: "API Integration",
      description: "RESTful APIs for seamless integration with your existing systems",
      features: [
        "Real-time case submission and retrieval",
        "Automated coding and documentation",
        "Status tracking and notifications",
        "Bulk processing capabilities"
      ],
      icon: FiLink,
      color: "blue"
    },
    {
      title: "SFTP Solutions",
      description: "Secure file transfer for batch processing and data exchange",
      features: [
        "Encrypted file transfers",
        "Automated batch processing",
        "Scheduled data synchronization",
        "Custom file format support"
      ],
      icon: FiFolder,
      color: "purple"
    },
    {
      title: "Practice Management Integration",
      description: "Direct integration with popular PM systems",
      features: [
        "Native PM system connectors",
        "Automated workflow triggers",
        "Seamless data flow",
        "Custom integration development"
      ],
      icon: FiMonitor,
      color: "green"
    }
  ];

  const timelineSteps = [
    {
      week: "Week 1",
      title: "Setup & Configuration",
      description: "Get cases, import any specific instructions and guidelines for coding etc",
      tasks: [
        "Initial system setup and configuration",
        "Import coding guidelines and payer requirements",
        "Configure user access and permissions",
        "Establish data flow connections"
      ],
      icon: FiSettings,
      color: "blue"
    },
    {
      week: "Week 2", 
      title: "Validation & Integration",
      description: "Validate outputs and integration touchpoints",
      tasks: [
        "Test integration endpoints",
        "Validate coding accuracy and outputs",
        "Review workflow automation",
        "Confirm data mapping and formats"
      ],
      icon: FiShield,
      color: "purple"
    },
    {
      week: "Week 3",
      title: "Fine-tuning & Approval",
      description: "Fine tune and approval step",
      tasks: [
        "Optimize coding algorithms for your cases",
        "Refine approval workflows",
        "Configure custom business rules",
        "Final stakeholder review and sign-off"
      ],
      icon: FiZap,
      color: "orange"
    },
    {
      week: "Week 4",
      title: "Production Testing",
      description: "Production testing",
      tasks: [
        "Deploy to production environment",
        "Run comprehensive system tests",
        "Monitor performance and accuracy",
        "Address any final adjustments"
      ],
      icon: FiCheckCircle,
      color: "red"
    },
    {
      week: "Week 5",
      title: "Go Live",
      description: "Customers see coding in their systems, have access to all tools in Revedy",
      tasks: [
        "Full system activation",
        "User training and onboarding",
        "Complete access to Revedy platform",
        "Ongoing support and monitoring"
      ],
      icon: FiPlay,
      color: "green"
    }
  ];

  const IntegrationCard = ({ option }) => (
    <Card shadow="xl" h="full" borderTop="4px" borderTopColor={`${option.color}.400`}>
      <CardBody p={6}>
        <VStack spacing={6} h="full">
          {/* Main Icon and Title */}
          <VStack spacing={4} flex={1} justify="center">
            <Box
              p={8}
              borderRadius="2xl"
              bg={`${option.color}.100`}
              color={`${option.color}.600`}
              shadow="lg"
            >
              <Icon as={option.icon} boxSize={16} />
            </Box>
            <Heading size="md" color="gray.800" textAlign="center" lineHeight="short">
              {option.title}
            </Heading>
            <Text color="gray.600" textAlign="center" fontSize="sm">
              {option.description}
            </Text>
          </VStack>

          {/* Features */}
          <VStack spacing={3} w="full" align="stretch">
            <List spacing={2}>
              {option.features.map((feature, idx) => (
                <ListItem key={idx} fontSize="sm" color="gray.600">
                  <ListIcon as={FiCheckCircle} color={`${option.color}.400`} />
                  {feature}
                </ListItem>
              ))}
            </List>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );

  const TimelineStep = ({ step, index }) => (
    <Card shadow="lg" borderLeft="4px" borderLeftColor={`${step.color}.400`} w="full">
      <CardBody p={6}>
        <HStack spacing={4} align="start">
          {/* Week Badge and Icon */}
          <VStack spacing={2} minW="100px" flexShrink={0}>
            <Badge colorScheme={step.color} fontSize="sm" px={3} py={1}>
              {step.week}
            </Badge>
            <Center w={12} h={12} bg={`${step.color}.100`} borderRadius="full">
              <Icon as={step.icon} boxSize={6} color={`${step.color}.600`} />
            </Center>
          </VStack>

          {/* Content */}
          <VStack spacing={3} align="start" flex={1}>
            <Heading size="md" color="gray.800">
              {step.title}
            </Heading>
            <Text color="gray.600" fontSize="sm" fontStyle="italic">
              {step.description}
            </Text>
            <List spacing={1}>
              {step.tasks.map((task, idx) => (
                <ListItem key={idx} fontSize="sm" color="gray.600">
                  <ListIcon as={FiCheckCircle} color={`${step.color}.400`} />
                  {task}
                </ListItem>
              ))}
            </List>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );

  return (
    <Grid
      templateAreas={`"header header"
                      "main main"`}
      gridTemplateRows={'60px 1fr'}
      gridTemplateColumns={'1fr'}
      h='100vh'
      gap='1'
    >
      <HeaderMenus />
      <GridItem area={'main'} bg="gray.50" overflowY="auto">
        <Container maxW="7xl" py={12}>
          {/* Header */}
          <VStack spacing={6} mb={12} textAlign="center">
            <Heading size="2xl" color="gray.800">
              Revedy Implementation
            </Heading>
            <Badge colorScheme="blue" fontSize="lg" px={4} py={2} borderRadius="full">
              30-Day Deployment Process
            </Badge>
            <Text fontSize="xl" color="gray.600" maxW="4xl">
              Seamless integration and rapid deployment for your medical coding automation.
              Get up and running in just 30 days with full support every step of the way.
            </Text>
          </VStack>

          {/* Integration Options Section */}
          <VStack spacing={8} mb={16}>
            <VStack spacing={4} textAlign="center">
              <Heading size="lg" color="gray.800">
                Integration Capabilities
              </Heading>
              <Text color="gray.600" maxW="3xl">
                Choose the integration method that best fits your existing infrastructure and workflow requirements.
              </Text>
            </VStack>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              {integrationOptions.map((option, index) => (
                <IntegrationCard key={index} option={option} />
              ))}
            </SimpleGrid>
          </VStack>

          {/* Implementation Timeline Section */}
          <VStack spacing={8} mb={16}>
            <VStack spacing={4} textAlign="center">
              <Heading size="lg" color="gray.800">
                30-Day Implementation Timeline
              </Heading>
              <Text color="gray.600" maxW="3xl">
                Our proven implementation process gets you operational quickly with comprehensive support and validation at every stage.
              </Text>
            </VStack>
            
            <VStack spacing={6} w="full" maxW="5xl" align="center">
              {timelineSteps.map((step, index) => (
                <TimelineStep key={index} step={step} index={index} />
              ))}
            </VStack>
          </VStack>

          {/* Call to Action */}
          <Center>
            <Card shadow="2xl" bgGradient="linear(to-r, blue.500, purple.500)" maxW="2xl">
              <CardBody p={10}>
                <VStack spacing={6} textAlign="center">
                  <Heading size="lg" color="white">
                    Ready to Get Started?
                  </Heading>
                  <Text color="blue.100" fontSize="lg">
                    Our implementation team is ready to work with you to customize Revedy for your specific needs 
                    and get you operational in just 30 days.
                  </Text>
                  <HStack spacing={6} wrap="wrap" justify="center">
                    <Box
                      as="button"
                      bg="white"
                      color="blue.600"
                      px={8}
                      py={4}
                      borderRadius="xl"
                      fontWeight="bold"
                      fontSize="lg"
                      shadow="lg"
                      _hover={{ 
                        shadow: "xl", 
                        transform: "scale(1.05)" 
                      }}
                      transition="all 0.3s"
                    >
                      Schedule Implementation Call
                    </Box>
                    <Box
                      as="button"
                      border="2px"
                      borderColor="white"
                      color="white"
                      px={8}
                      py={4}
                      borderRadius="xl"
                      fontWeight="bold"
                      fontSize="lg"
                      _hover={{ 
                        bg: "whiteAlpha.200",
                        transform: "scale(1.05)" 
                      }}
                      transition="all 0.3s"
                    >
                      View Technical Documentation
                    </Box>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </Center>
        </Container>
      </GridItem>
    </Grid>
  );
};

export default RevedyImplementation;
